import { Alert, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { HelperResolution } from "utils/worksheet/worksheetHelpers";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { Controller, useFormContext } from "react-hook-form";
import { WorksheetInputField } from "./WorksheetFormFields";

type WorksheetTravelTabProps = {
  values: WorksheetItemInput;
  hourlyRows: { index: number }[];
  resolution: HelperResolution;
  isReadOnly?: boolean;
  renderToggle: (index: number) => React.ReactNode;
};

export default function WorksheetTravelTab({
  values,
  hourlyRows,
  resolution,
  isReadOnly,
  renderToggle,
}: WorksheetTravelTabProps) {
  const { control } = useFormContext<WorksheetItemInput>();
  const travel = values.helpers?.travel;

  return (
    <>
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <UiTypography uiVariant="body-sm">Include travel</UiTypography>
        <Controller
          control={control}
          name="helpers.travel.enabled"
          render={({ field }) => (
            <Switch
              size="small"
              checked={field.value}
              disabled={isReadOnly}
              onChange={(event) => field.onChange(event.target.checked)}
            />
          )}
        />
      </MDBox>
      {travel?.enabled && (
        <MDBox
          display="grid"
          gridTemplateColumns="repeat(2, minmax(0, 1fr))"
          alignItems="start"
          gap="12px"
        >
          <WorksheetInputField
            name="helpers.travel.roundTripHours"
            label="Round-trip travel hours"
            placeholder="0"
            isReadOnly={isReadOnly}
          />
          <WorksheetInputField
            name="helpers.travel.visits"
            label="Visits"
            placeholder="One per workday"
            isReadOnly={isReadOnly}
          />
        </MDBox>
      )}
      {travel?.enabled && resolution.travelHours !== null && (
        <Alert severity="info">
          Travel hours: {resolution.travelHours.toDecimalPlaces(2).toString()}
        </Alert>
      )}
      <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
        Travel adds billed hours only to selected hourly costs. Material needs and production
        quantity stay unchanged.
      </UiTypography>
      {hourlyRows.length > 0 ? (
        hourlyRows.map(({ index }) => renderToggle(index))
      ) : (
        <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
          Add an hourly labor or equipment cost to link travel.
        </UiTypography>
      )}
    </>
  );
}
